import { logger } from '@heidi/runtime/logger';
import { type LanguageCode } from '@heidi/schema/ml-scribe';
import { useUpdateUserSettings } from '@heidi/workspace/data';
import { useCallback, useRef } from 'react';

/**
 * Persists the onboarding display language to the account the moment the
 * locale flips.
 *
 * Failures retry once, then degrade silently: the page copy is client-side
 * and stays localized either way, and completion persists the language again.
 */
export function useDisplayLanguagePersistence() {
  const { mutate } = useUpdateUserSettings({ showToast: false });
  const latestLanguageRef = useRef<LanguageCode | null>(null);

  const persistLanguage = useCallback(
    (language: LanguageCode) => {
      latestLanguageRef.current = language;
      let hasRetried = false;
      const fire = () => {
        mutate(
          { language_time_settings: { display_language: language } },
          {
            onError: () => {
              // A stale retry could land after a newer language's PATCH and
              // win — the newer persist owns the outcome now.
              if (latestLanguageRef.current !== language) return;
              if (hasRetried) {
                logger.warn(
                  `[display-language] failed to persist display language "${language}" mid-flow after retry — backend-translated content stays in the account language`
                );
                return;
              }
              hasRetried = true;
              fire();
            },
          }
        );
      };
      fire();
    },
    [mutate]
  );

  return { persistLanguage };
}
